'use client';

import type { UpdateNicknameServiceRequest } from '@musinsa/services-curator';
import { updateNicknameService } from '@musinsa/services-curator';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { useCuratorBroadcastPublisher } from '../../common';
import { useCuratorSnackbar } from '../../common/hooks';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';
import { NicknameEditButton } from './NicknameEditButton';
import { NicknameQuotaIndicator } from './NicknameQuotaIndicator';
import { NicknameRecommendationInput } from './NicknameRecommendationInput';
import { NicknameStatusDisplay } from './NicknameStatusDisplay';

export interface NicknameForm {
  nickname: string;
}

export const NicknameEditForm = ({ className }: { className?: string }) => {
  const { navigateBack } = useNavigate();
  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const snackbar = useCuratorSnackbar();

  const methods = useForm<NicknameForm>({
    defaultValues: {
      nickname: '',
    },
  });

  const {
    formState: { errors },
    handleSubmit,
  } = methods;

  const { data: availableCountData } = useQuery({
    ...curatorQueries.profile.nicknameAvailableCount,
  });

  const { data: userInfo } = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const snsTitle = userInfo?.snsTitle ?? '';

  const {
    mutate: updateNickname,
    isPending: isUpdateNicknamePending,
    reset,
    error: updateNicknameError,
  } = useMutation({
    mutationFn: (data: UpdateNicknameServiceRequest) => updateNicknameService(data),
    onSuccess: () => {
      snackbar.success('닉네임이 변경되었습니다.');
      triggerQuerySync({
        queryKeys: [
          ...(snsTitle ? [curatorQueries.profile.profileBySnsTitle(snsTitle).queryKey] : []),
          curatorQueries.profile.nicknameAvailableCount.queryKey,
          curatorQueries.profile.userInfo.queryKey,
        ],
      });
      navigateBack();
    },
    onError: (error: Error) => {
      snackbar.error(error.message);
    },
  });

  const onSubmit = (data: NicknameForm) => {
    updateNickname({ nickname: data.nickname.trim() });
  };

  return (
    <FormProvider {...methods}>
      <div
        className={twMerge(
          'flex flex-col gap-[8px] px-[16px] pt-[24px] pb-[72px] w-full grow',
          className,
        )}
      >
        <NicknameQuotaIndicator />

        <NicknameRecommendationInput
          hasError={!!updateNicknameError || !!errors.nickname?.message}
          disabled={isUpdateNicknamePending || availableCountData?.remainingCount === 0}
          onMutationReset={reset}
        />

        <NicknameStatusDisplay
          errorMessage={errors.nickname?.message ?? updateNicknameError?.message ?? null}
        />
      </div>
      <NicknameEditButton
        isLoading={isUpdateNicknamePending}
        handleSubmit={handleSubmit(onSubmit)}
        updateNicknameError={updateNicknameError}
      />
    </FormProvider>
  );
};
